/**
 * 环境变量配置工具
 * 统一管理和验证环境变量
 */

interface EnvironmentConfig {
  // 应用基础配置
  appTitle: string
  appVersion: string
  appEnv: 'development' | 'production' | 'staging'
  
  // API 配置
  apiBaseUrl: string
  apiPrefix: string
  adminApiPrefix: string
  wsBaseUrl: string
  
  // 认证配置
  authTokenKey: string
  authRefreshKey: string
  authUserKey: string
  tokenRefreshThreshold: number
  
  // 界面配置
  defaultTheme: 'light' | 'dark'
  sidebarCollapsed: boolean
  sidebarWidth: number
  defaultPageSize: number
  pageSizeOptions: number[]
  
  // 上传配置
  uploadMaxSize: number
  uploadAllowedTypes: string
  
  // 功能开关
  features: {
    darkMode: boolean
    multiLang: boolean
    advancedSearch: boolean
    exportData: boolean
  }
  
  // 缓存配置
  storagePrefix: string
  cacheExpireTime: number
  
  // 调试配置
  debugEnabled: boolean
  consoleLogLevel: string
}

/**
 * 获取环境变量值，如果不存在则返回默认值
 */
function getEnvValue(key: string, defaultValue: string = ''): string {
  return import.meta.env[key] || defaultValue
}

/**
 * 获取环境变量数字值
 */
function getEnvNumber(key: string, defaultValue: number = 0): number {
  const value = getEnvValue(key)
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? defaultValue : parsed
}

/**
 * 获取环境变量布尔值
 */
function getEnvBoolean(key: string, defaultValue: boolean = false): boolean {
  const value = getEnvValue(key).toLowerCase()
  if (value === 'true' || value === '1') return true
  if (value === 'false' || value === '0') return false
  return defaultValue
}

/**
 * 获取环境变量数组值
 */
function getEnvArray(key: string, defaultValue: string[] = []): string[] {
  const value = getEnvValue(key)
  return value ? value.split(',').map(item => item.trim()) : defaultValue
}

/**
 * 获取环境变量数字数组值
 */
function getEnvNumberArray(key: string, defaultValue: number[] = []): number[] {
  const strArray = getEnvArray(key)
  return strArray.length > 0 
    ? strArray.map(str => Number.parseInt(str, 10)).filter(num => !Number.isNaN(num))
    : defaultValue
}

/**
 * 环境配置对象
 */
export const env: EnvironmentConfig = {
  // 应用基础配置
  appTitle: getEnvValue('VITE_APP_TITLE', 'AcquiLang AI 管理中台'),
  appVersion: getEnvValue('VITE_APP_VERSION', '1.0.0'),
  appEnv: getEnvValue('VITE_APP_ENV', 'development') as EnvironmentConfig['appEnv'],
  
  // API 配置
  apiBaseUrl: getEnvValue('VITE_API_BASE_URL', 'http://localhost:8000'),
  apiPrefix: getEnvValue('VITE_API_PREFIX', '/api/v1'),
  adminApiPrefix: getEnvValue('VITE_ADMIN_API_PREFIX', '/api/v1/admin'),
  wsBaseUrl: getEnvValue('VITE_WS_BASE_URL', 'ws://localhost:8000/ws'),
  
  // 认证配置
  authTokenKey: getEnvValue('VITE_AUTH_TOKEN_KEY', 'admin_token'),
  authRefreshKey: getEnvValue('VITE_AUTH_REFRESH_KEY', 'admin_refresh_token'),
  authUserKey: getEnvValue('VITE_AUTH_USER_KEY', 'admin_user'),
  tokenRefreshThreshold: getEnvNumber('VITE_TOKEN_REFRESH_THRESHOLD', 300),
  
  // 界面配置
  defaultTheme: getEnvValue('VITE_DEFAULT_THEME', 'light') as EnvironmentConfig['defaultTheme'],
  sidebarCollapsed: getEnvBoolean('VITE_SIDEBAR_COLLAPSED', false),
  sidebarWidth: getEnvNumber('VITE_SIDEBAR_WIDTH', 240),
  defaultPageSize: getEnvNumber('VITE_DEFAULT_PAGE_SIZE', 20),
  pageSizeOptions: getEnvNumberArray('VITE_PAGE_SIZE_OPTIONS', [10, 20, 50, 100]),
  
  // 上传配置
  uploadMaxSize: getEnvNumber('VITE_UPLOAD_MAX_SIZE', 10485760), // 10MB
  uploadAllowedTypes: getEnvValue('VITE_UPLOAD_ALLOWED_TYPES', 'image/*,application/pdf,text/*'),
  
  // 功能开关
  features: {
    darkMode: getEnvBoolean('VITE_FEATURE_DARK_MODE', true),
    multiLang: getEnvBoolean('VITE_FEATURE_MULTI_LANG', true),
    advancedSearch: getEnvBoolean('VITE_FEATURE_ADVANCED_SEARCH', true),
    exportData: getEnvBoolean('VITE_FEATURE_EXPORT_DATA', true),
  },
  
  // 缓存配置
  storagePrefix: getEnvValue('VITE_STORAGE_PREFIX', 'acquilang_admin_'),
  cacheExpireTime: getEnvNumber('VITE_CACHE_EXPIRE_TIME', 3600),
  
  // 调试配置
  debugEnabled: getEnvBoolean('VITE_DEBUG_ENABLED', true),
  consoleLogLevel: getEnvValue('VITE_CONSOLE_LOG_LEVEL', 'debug'),
}

/**
 * 验证环境配置
 */
export function validateEnvironment(): { valid: boolean; errors: string[] } {
  const errors: string[] = []
  
  // 必需的配置检查
  if (!env.apiBaseUrl) {
    errors.push('VITE_API_BASE_URL is required')
  }
  
  if (!env.appTitle) {
    errors.push('VITE_APP_TITLE is required')
  }
  
  // URL 格式检查
  try {
    new URL(env.apiBaseUrl)
  } catch {
    errors.push('VITE_API_BASE_URL must be a valid URL')
  }
  
  // 主题配置检查
  if (!['light', 'dark'].includes(env.defaultTheme)) {
    errors.push('VITE_DEFAULT_THEME must be "light" or "dark"')
  }
  
  // 环境类型检查
  if (!['development', 'production', 'staging'].includes(env.appEnv)) {
    errors.push('VITE_APP_ENV must be "development", "production", or "staging"')
  }
  
  // 分页配置检查
  if (env.defaultPageSize <= 0) {
    errors.push('VITE_DEFAULT_PAGE_SIZE must be greater than 0')
  }
  
  if (env.pageSizeOptions.length === 0) {
    errors.push('VITE_PAGE_SIZE_OPTIONS must not be empty')
  }
  
  // 上传配置检查
  if (env.uploadMaxSize <= 0) {
    errors.push('VITE_UPLOAD_MAX_SIZE must be greater than 0')
  }
  
  return {
    valid: errors.length === 0,
    errors,
  }
}

/**
 * 开发环境检查
 */
export const isDevelopment = env.appEnv === 'development'

/**
 * 生产环境检查
 */
export const isProduction = env.appEnv === 'production'

/**
 * 获取完整的 API URL
 */
export function getApiUrl(path: string = ''): string {
  const baseUrl = env.apiBaseUrl.endsWith('/') 
    ? env.apiBaseUrl.slice(0, -1) 
    : env.apiBaseUrl
  const prefix = env.apiPrefix.startsWith('/') 
    ? env.apiPrefix 
    : `/${env.apiPrefix}`
  const fullPath = path.startsWith('/') ? path : `/${path}`
  
  return `${baseUrl}${prefix}${fullPath}`
}

/**
 * 获取管理员 API URL
 */
export function getAdminApiUrl(path: string = ''): string {
  const baseUrl = env.apiBaseUrl.endsWith('/') 
    ? env.apiBaseUrl.slice(0, -1) 
    : env.apiBaseUrl
  const prefix = env.adminApiPrefix.startsWith('/') 
    ? env.adminApiPrefix 
    : `/${env.adminApiPrefix}`
  const fullPath = path.startsWith('/') ? path : `/${path}`
  
  return `${baseUrl}${prefix}${fullPath}`
}

/**
 * 获取 WebSocket URL
 */
export function getWebSocketUrl(path: string = ''): string {
  const baseUrl = env.wsBaseUrl.endsWith('/') 
    ? env.wsBaseUrl.slice(0, -1) 
    : env.wsBaseUrl
  const fullPath = path.startsWith('/') ? path : `/${path}`
  
  return `${baseUrl}${fullPath}`
}

/**
 * 控制台日志工具（基于环境配置）
 */
export const logger = {
  debug: (...args: any[]) => {
    if (env.debugEnabled && ['debug', 'verbose'].includes(env.consoleLogLevel)) {
      console.log('[DEBUG]', ...args)
    }
  },
  info: (...args: any[]) => {
    if (env.debugEnabled && ['debug', 'verbose', 'info'].includes(env.consoleLogLevel)) {
      console.info('[INFO]', ...args)
    }
  },
  warn: (...args: any[]) => {
    if (env.debugEnabled) {
      console.warn('[WARN]', ...args)
    }
  },
  error: (...args: any[]) => {
    if (env.debugEnabled) {
      console.error('[ERROR]', ...args)
    }
  },
}

// 在开发环境下验证配置
if (isDevelopment) {
  const validation = validateEnvironment()
  if (validation.valid) {
    logger.debug('Environment validation passed')
  } else {
    logger.error('Environment validation failed:', validation.errors)
  }
  
  // 输出当前环境配置（仅在 debug 模式下）
  if (env.consoleLogLevel === 'debug') {
    logger.debug('Current environment config:', env)
  }
}

export default env